import { useState, useEffect, useRef, FormEvent } from "react";
import { Sparkles, Brain, Mail, CheckCircle, Download, Eye, FileText } from "lucide-react";

import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import ReportPreview from "./ReportPreview";
import RetroConfetti from "./RetroConfetti";
import TiltCard from "./TiltCard";

interface NewsletterCaptureProps {
  topic?: string;
  query?: string;
  theme?: string;
  podcastArtworks?: string[];
}

const STATUS_MESSAGES = [
  { icon: Sparkles, text: "Firing up the podcast brain… ☕", delay: 0 },
  { icon: Brain, text: "Speed-reading 10,000+ minutes of operator wisdom…", delay: 3000 },
  { icon: Sparkles, text: "Stealing tactics from 7- and 8-figure founders…", delay: 7000 },
  { icon: Brain, text: "Pulling ROAS targets, LTV benchmarks & real numbers…", delay: 11000 },
  { icon: Sparkles, text: "Sorting quick wins from long-term plays…", delay: 15000 },
  { icon: Mail, text: "Packaging your unfair advantage into one email…", delay: 19000 },
  { icon: CheckCircle, text: "Done! Check your inbox — it just got dangerously useful 🔥", delay: 23000 },
];

export default function NewsletterCapture({ topic, query, theme, podcastArtworks }: NewsletterCaptureProps) {
  const { user } = useAuth();
  const userEmail = user?.email || "";
  const [email, setEmail] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusIndex, setStatusIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [reportHtml, setReportHtml] = useState<string | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);


  useEffect(() => {
    if (userEmail) setEmail(userEmail);
  }, [userEmail]);

  useEffect(() => {
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  const startStatusCycle = () => {
    setIsGenerating(true);
    setStatusIndex(0);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    STATUS_MESSAGES.forEach((_, i) => {
      if (i === 0) return;
      const timer = setTimeout(() => setStatusIndex(i), STATUS_MESSAGES[i].delay);
      timersRef.current.push(timer);
    });
  };

  const stopStatusCycle = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setStatusIndex(STATUS_MESSAGES.length - 1);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3500);
    setTimeout(() => setIsGenerating(false), 2500);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isGenerating) return;

    const submittedEmail = email.trim();

    // Log alternate email if user changed it from their account email
    if (user && submittedEmail.toLowerCase() !== userEmail.toLowerCase()) {
      try {
        await supabase.from("report_only_emails" as any).insert({
          user_id: user.id,
          email: submittedEmail,
          search_query: query || null,
        });
      } catch (err) {
        console.error("Report email log error:", err);
      }
    }

    try {
      const { error } = await supabase.from("newsletter_signups" as any).insert({
        email: submittedEmail,
        search_query: query || null,
        theme: theme || topic || null,
      });
      if (error) {
        if (error.code === "23505") {
          toast.success("Building your personalized breakdown — check your inbox shortly! 📬");
        } else {
          throw error;
        }
      } else {
        toast.success("On it! We're pulling the best insights for you now ✨");
      }
      setEmail("");
      setSubmitted(true);
      setIsEditing(false);
      startStatusCycle();

      const invokeWithRetry = async (attempt = 1): Promise<void> => {
        try {
          const { data, error } = await supabase.functions.invoke("generate-expert-answer", {
            body: {
              email: submittedEmail,
              query: query || topic || "ecommerce growth strategies",
              theme: theme || topic || null,
            },
          });
          if (error) {
            const is404 = error.message?.includes("404") || error.message?.includes("NOT_FOUND");
            if (is404 && attempt < 3) {
              await new Promise(r => setTimeout(r, attempt * 2000));
              return invokeWithRetry(attempt + 1);
            }
            console.error("Expert answer error:", error.message);
          }
          if (data?.emailHtml) setReportHtml(data.emailHtml as string);
          stopStatusCycle();

        } catch (err) {
          if (attempt < 3) {
            await new Promise(r => setTimeout(r, attempt * 2000));
            return invokeWithRetry(attempt + 1);
          }
          console.error("Expert answer trigger error:", err);
          stopStatusCycle();
        }
      };
      invokeWithRetry();
    } catch (err) {
      console.error("Newsletter signup error:", err);
      toast.error("Something went wrong. Please try again.");
      setIsGenerating(false);
    }
  };

  const displayTheme = theme || query || topic || "ecommerce";

  // Reshape the topic so it slots naturally after "a full report on ___".
  // Strips question/imperative prefixes, trailing punctuation, lowercases,
  // then converts a leading bare verb into its gerund form so the sentence
  // reads grammatically (e.g. "reduce CAC" -> "reducing CAC").
  const topicForSentence = (() => {
    let t = (displayTheme || "").trim().replace(/[?!.]+$/g, "").toLowerCase();
    // Remove common leading question/imperative words
    t = t.replace(
      /^(how (do|can|to|should) (i|you|we)?\s*|how to\s+|what (is|are|s)\s+the\s+best\s+way[s]?\s+to\s+|what (is|are|s)\s+|why (do|does|is|are)\s+|when (do|should|to)\s+|where (do|to)\s+|the best way[s]? to\s+|ways to\s+|tips (for|on|to)\s+|guide to\s+|best\s+|top\s+)/,
      ""
    );
    // Drop a leading article
    t = t.replace(/^(a |an |the )/, "");
    t = t.trim();

    // Convert a leading bare verb into a gerund so it slots in grammatically.
    // Only touches the first word, and only if it's a recognized action verb,
    // so noun-phrase topics ("shopify seo", "amazon ppc") are left alone.
    const ACTION_VERBS = new Set([
      "reduce","lower","increase","grow","scale","boost","improve","optimize","optimise",
      "build","create","launch","start","run","manage","drive","fix","solve","handle",
      "find","choose","pick","select","write","design","plan","track","measure","test",
      "convert","retain","acquire","sell","ship","price","negotiate","hire",
      "outsource","automate","integrate","migrate","rank","advertise","market","brand",
      "rebrand","monetize","monetise","crack","tackle","master","leverage","use","avoid",
      "prevent","stop","cut","save","spend","invest","raise","fund","bootstrap","expand",
      "diversify","segment","target","retarget","upsell","bundle",
      "discount","review","analyze","analyse","forecast","return",
    ]);
    const toGerund = (verb: string): string => {
      if (/ie$/.test(verb)) return verb.slice(0, -2) + "ying";
      // silent 'e' -> drop + ing (reduce -> reducing, scale -> scaling)
      if (/[^aeiou]e$/.test(verb)) return verb.slice(0, -1) + "ing";
      // short CVC -> double final consonant (run -> running, ship -> shipping)
      if (/^[^aeiou]?[aeiou][^aeiouwxy]$/.test(verb)) return verb + verb.slice(-1) + "ing";
      return verb + "ing";
    };

    const firstSpace = t.indexOf(" ");
    const firstWord = firstSpace === -1 ? t : t.slice(0, firstSpace);
    const rest = firstSpace === -1 ? "" : t.slice(firstSpace);
    if (ACTION_VERBS.has(firstWord)) {
      t = toGerund(firstWord) + rest;
    }

    return t || displayTheme;
  })();

  const currentStatus = STATUS_MESSAGES[statusIndex];
  const StatusIcon = currentStatus.icon;

  return (
    <TiltCard className="hover-lift transition-all duration-200">
      <div
        className={[
          "relative rounded-md p-3 sm:p-5 overflow-hidden",
          // Light comic-book blue wash — distinct from peach question cards
          // and cream podcast cards, so the report block reads as its own object.
          "bg-[hsl(var(--ink-blue)/0.08)]",
          // Dashed blue border + offset blue drop shadow, mirroring the
          // AnsweredQuestion treatment but in the report's own ink-blue hue.
          "border-2 border-dashed border-[hsl(var(--ink-blue)/0.55)]",
          "shadow-[4px_4px_0_0_hsl(var(--foreground))] hover:shadow-[6px_6px_0_0_hsl(var(--foreground))]",
          "hover:-translate-x-[2px] hover:-translate-y-[2px] transition-all",
        ].join(" ")}
      >
        <RetroConfetti active={showConfetti} />

        {/* Left accent stripe — full-height solid ink-blue bar, mirrors callout */}
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-1.5 bg-[hsl(var(--ink-blue))]"
        />

        {/* Decorative document watermark in the corner — instantly signals "report" */}
        <FileText
          aria-hidden
          className="absolute -top-3 -right-3 w-20 h-20 text-[hsl(var(--ink-blue)/0.10)] rotate-12 pointer-events-none"
        />

        {/* Top row: Report preview + title/copy */}
        <div className="flex gap-3 sm:gap-4 items-start mb-3 relative pl-2 sm:pl-3">
          <ReportPreview
            theme={displayTheme}
            query={query || topic || "ecommerce growth strategies"}
            podcastArtworks={podcastArtworks}
          />

          <div className="flex-1 min-w-0">
            <h3 className="font-retro font-bold text-base sm:text-lg mb-1.5 text-[hsl(var(--ink-blue))]">
              Get the full answer delivered to your inbox 📩
            </h3>
            <p className="text-xs sm:text-sm text-foreground leading-snug">
              Get the full answer from thousands of podcasts, plus the playlist of the best episodes to listen to on <span className="font-bold text-accent">{topicForSentence}</span>.
            </p>
          </div>
        </div>


      {/* Bottom row: Email form always full-width */}
      {!submitted ? (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
          {userEmail && !isEditing ? (
            <div
              className="flex-1 font-mono text-xs sm:text-sm px-3 py-2 bg-muted/50 border-2 border-border text-muted-foreground truncate cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => {
                setIsEditing(true);
                setTimeout(() => inputRef.current?.focus(), 50);
              }}
              title="Click to change email"
            >
              {email || userEmail}
            </div>
          ) : (
            <input
              ref={inputRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSubmit(e); } }}
              onBlur={() => { if (userEmail && !email.trim()) { setEmail(userEmail); setIsEditing(false); } }}
              placeholder="you@brand.com"
              className="flex-1 font-mono text-base sm:text-sm px-3 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 bg-background border-2 border-border focus:border-primary focus:outline-none"
            />
          )}
          <button
            type="submit"
            className="comic-play-btn relative overflow-hidden font-mono font-bold text-xs sm:text-sm px-4 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 bg-primary text-primary-foreground border-[3px] border-[hsl(24,80%,35%)] rounded-md shadow-[4px_4px_0px_0px_hsl(var(--foreground))] hover:shadow-[2px_2px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] hover:rotate-[-0.5deg] transition-all duration-150 transform hover:scale-[1.03] active:scale-95 flex-shrink-0"
          >
            <span className="relative z-10">{userEmail ? "Send me the full report!" : "Send me the insights"}</span>
          </button>
        </form>
      ) : isGenerating ? (
        <div className="flex items-center gap-2 py-2 animate-fade-in">
          <StatusIcon className="w-4 h-4 text-accent flex-shrink-0 animate-pulse" />
          <p className="font-mono text-xs sm:text-sm text-muted-foreground animate-fade-in" key={statusIndex}>
            {currentStatus.text}
          </p>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 animate-fade-in">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Mail className="w-4 h-4 text-accent flex-shrink-0" />
            <p className="font-mono text-xs sm:text-sm text-accent font-bold truncate">
              Your report is waiting in your inbox 📬
            </p>
          </div>
          {reportHtml && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  const w = window.open("", "_blank");
                  if (w) { w.document.write(reportHtml); w.document.close(); }
                }}
                title="Open the report in a new tab"
                className="font-mono font-bold text-[10px] sm:text-xs px-3 py-2.5 sm:py-1.5 min-h-[40px] sm:min-h-0 bg-background border-2 border-border hover:border-primary hover:bg-muted/50 transition-colors flex items-center gap-1.5"
              >
                <Eye className="w-3.5 h-3.5" /> View
              </button>
              <button
                type="button"
                onClick={() => {
                  const safe = (theme || query || topic || "askthepods-report")
                    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
                  const blob = new Blob([reportHtml], { type: "text/html;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `askthepods-${safe || "report"}.html`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                title="Download the full report as an HTML file"
                className="font-mono font-bold text-[10px] sm:text-xs px-3 py-2.5 sm:py-1.5 min-h-[40px] sm:min-h-0 bg-primary text-primary-foreground border-2 border-[hsl(24,80%,35%)] hover:brightness-110 transition-all flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </TiltCard>
  );
}
